// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  AuthnRequest,
  AuthnResponse,
  RevokeCredentialRequest,
  BadgePinRequest,
  OtpPinRequest,
  OtpRegistrationKeyRequest,
  RegistrationResponse,
  UpdateCredentialNicknameRequest,
  UserResponse,
} from "./idp.types";
import { getAppConfig } from "../config";
import { IdpApiConstants } from "../../lib/constants";
import { getUserAccessToken } from "../auth-profile";

const config = getAppConfig();

// Define a service using a base URL and expected endpoints
export const idpApi = createApi({
  reducerPath: "idpApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.idpUrl,
    credentials: "include",
  }), // TODO: change this to use config value
  // tagTypes is needed by invalidating tags?
  tagTypes: [
    "LoginTOTP",
    "RegisterMFA",
    "RegisterOTP",
    "MakeCredentialsChallenge",
    "WebAuthnResponse",
    "Login",
    "Users",
  ],
  endpoints: (builder) => ({
    registerMfa: builder.mutation<RegistrationResponse, void>({
      query: () => ({
        url: IdpApiConstants.REGISTER_MFA_ENDPOINT,
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
      }),
      invalidatesTags: ["RegisterMFA", "Users"],
    }),
    registerOTPKey: builder.mutation<void, OtpRegistrationKeyRequest>({
      query: ({ ...otpKeyRegistrationRequest }) => ({
        url: IdpApiConstants.REGISTER_OTP_KEY_ENDPOINT,
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
        body: otpKeyRegistrationRequest,
      }),
      invalidatesTags: ["RegisterOTP"],
    }),
    setOTPPin: builder.mutation<void, OtpPinRequest>({
      query: ({ ...otpPinRequest }) => ({
        url: IdpApiConstants.REGISTER_OTP_PIN_ENDPOINT,
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
        body: otpPinRequest,
      }),
      invalidatesTags: ["RegisterOTP", "Users"],
    }),
    setBadgePin: builder.mutation<void, BadgePinRequest>({
      query: ({ ...badgePinRequest }) => ({
        url: IdpApiConstants.REGISTER_BADGE_PIN_ENDPOINT,
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
        body: badgePinRequest,
      }),
    }),
    sendAuthnResponse: builder.mutation<AuthnResponse, AuthnRequest>({
      query: ({ ...authnRequest }) => ({
        url: IdpApiConstants.REGISTER_MFA_ENDPOINT,
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
        body: authnRequest,
      }),
      invalidatesTags: ["WebAuthnResponse"],
    }),
    directUsers: builder.query<any, { includeDisabled?: boolean }>({
      query: ({ includeDisabled }) => ({
        url: `${IdpApiConstants.USERS_MYDIRECTS}${includeDisabled ? "?includeDisabled=true" : ""}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
      }),
      providesTags: [{ type: "Users", id: "Directs" }],
    }),
    getUser: builder.query<UserResponse, string>({
      query: (alias) => ({
        url: `${IdpApiConstants.USERS_BASE}/${alias}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
      }),
      providesTags: (_result, _error, alias) => {
        return [{ type: "Users", id: alias }];
      },
    }),
    sendRegistrationNotification: builder.mutation<
      { registrationLink: string },
      { alias: string; notificationTemplateName: string }
    >({
      query: (body) => ({
        url: IdpApiConstants.REGCODE_GENERATE,
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
        body: body,
      }),
    }),
    revokeCredential: builder.mutation<void, RevokeCredentialRequest>({
      query: ({ alias, credentialId, reason }) => ({
        url: IdpApiConstants.WEBAUTHN_CREDENTIALS_BASE,
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
        body: { alias, credentialId, reason },
      }),
      invalidatesTags: (_result, error, { alias }) =>
        // don't invalidate tags on an error
        error
          ? []
          : [
              { type: "Users", id: alias },
              { type: "Users", id: "Directs" },
            ],
    }),
    updateCredentialNickname: builder.mutation<
      void,
      UpdateCredentialNicknameRequest
    >({
      query: ({ credentialId, nickname }) => ({
        url: `${IdpApiConstants.WEBAUTHN_CREDENTIALS_BASE}`,
        method: "PATCH",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getUserAccessToken()}`,
        },
        body: { credentialId, nickname },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useRegisterMfaMutation,
  useRegisterOTPKeyMutation,
  useSetOTPPinMutation,
  useSetBadgePinMutation,
  useSendAuthnResponseMutation,
  useDirectUsersQuery,
  useGetUserQuery,
  useSendRegistrationNotificationMutation,
  useRevokeCredentialMutation,
  useUpdateCredentialNicknameMutation,
} = idpApi;
